import type { CreditRangeReadOnlyRow } from '@/components/CreditRangesReadOnlyTable/credit-ranges-read-only-table';
import { CREDIT_GRADES } from '@/constants/plan-of-study-form-constants';
import type { GradeSetting } from '@/repositories/configurations-repository';
import type { PlanOfStudyGradeSetting } from '@/types/plans-of-study';

function gradeLevelHasCreditValue(min?: number | null, max?: number | null): boolean {
  return (min !== null && min !== undefined) || (max !== null && max !== undefined);
}

export function planGradeSettingsHasDefinedCredits(
  gradeSettings?: PlanOfStudyGradeSetting[] | null
): boolean {
  if (!gradeSettings?.length) {
    return false;
  }
  return gradeSettings.some((g) =>
    gradeLevelHasCreditValue(g.minCredits ?? null, g.maxCredits ?? null)
  );
}

/** True when this plan uses its own credit table and at least one grade has min or max set. */
export function planHasPlanSpecificCreditRanges(plan: {
  showGradeSetting?: boolean;
  gradeSettings?: PlanOfStudyGradeSetting[] | null;
}): boolean {
  return Boolean(plan.showGradeSetting && planGradeSettingsHasDefinedCredits(plan.gradeSettings));
}

export function districtGradeSettingsHasDefinedCredits(
  gradeSettings?: GradeSetting[] | null
): boolean {
  if (!gradeSettings?.length) {
    return false;
  }
  return gradeSettings.some((g) =>
    gradeLevelHasCreditValue(g.minCredits ?? null, g.maxCredits ?? null)
  );
}

/** One row per secondary grade (6–12); fills from district GET course-planner/config gradeSettings. */
export function buildCreditRangeRowsFromDistrictSettings(
  gradeSettings: GradeSetting[] | undefined
): CreditRangeReadOnlyRow[] {
  const byGrade = new Map<number, GradeSetting>();
  for (const g of gradeSettings ?? []) {
    byGrade.set(g.grade, g);
  }
  return CREDIT_GRADES.map((grade) => {
    const row = byGrade.get(grade);
    return {
      grade,
      minCredits: row?.minCredits ?? null,
      maxCredits: row?.maxCredits ?? null,
    };
  });
}
